import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Box, Checkbox, Stack, Typography } from '@mui/material';
import Button from '@/components/button/Button';
import { styles } from './styles';
import { SignUpProps, UserSignPasswordInterface } from '../SignUpInterface';
import TextField from '@/components/textField/TextField';
import { SignUpPasswordController as SignUpController } from './SignUpPasswordController';
import { hasLetter, hasMinLength, hasNumber } from '@/utils/passwordValidation';
import { Close, Check } from '@mui/icons-material';
import { useUserService } from '@/data/services/useUserService';
import { AppError } from '@/utils/AppError';
import WarningField from '@/components/warningField/WarningField';
import { mapSignUpToUserBackEnd } from '@/data/DTOs/UserDTO';
import { useNavigate } from 'react-router-dom';

const SignUpPassword = ({
  changeStep,
  user,
  setUser,
  loading,
  setLoading,
  showWarning,
  setShowWarning,
  warningMessage,
  setWarningMessage,
  createdUser,
  setCreatedUser,
}: SignUpProps) => {
  const { t } = useTranslation(['signInPassword', 'common', 'termsOfUse']);
  const navigate = useNavigate();
  const { watch, isValid, controlledTextView, handleSubmit } = SignUpController(
    { hasLetter, hasNumber, user, setUser }
  );
  const [allowTermsConditions, setAllowTermsCondition] = useState(
    user.password != ''
  );
  const [nextScreen, setNextScreen] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState(user.password);
  const { signUp } = useUserService();

  const password = watch('password', user.password);

  const handleCreateAccount = () => {
    if (createdUser) {
      changeStep(true);
      return;
    }
    setLoading(true);
    const mappedUser = mapSignUpToUserBackEnd(user);
    signUp(mappedUser)
      .then(() => {
        if (setCreatedUser) setCreatedUser(true);
        changeStep(true);
      })
      .catch((error: AppError) => {
        setShowWarning(true);
        setWarningMessage(
          t(error.message) ?? t('common:default_error_message')
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (nextScreen) {
      setNextScreen(false);
      handleCreateAccount();
    }
  }, [user]);

  const onSubmit = (data: UserSignPasswordInterface) => {
    setUser({
      ...user,
      password: data.password,
    });
    setNextScreen(true);
  };

  const customPermissions = (
    <Typography component="div" sx={styles.checkBoxLabel}>
      <Box sx={styles.checkBoxText}>{t('create_account_allow_text')} </Box>
      <Box
        component={'a'}
        sx={styles.checkRedText}
        onClick={() => {
          sessionStorage.setItem('signupStep', '2');
          sessionStorage.setItem('signupUser', JSON.stringify(user));
          navigate('/privacy-policy');
        }}
      >
        {t('create_account_allow_privacy')}{' '}
      </Box>
      <Box sx={styles.checkBoxText}>{t('create_account_terms_middle')} </Box>
      <Box
        component={'a'}
        sx={styles.checkRedText}
        onClick={() => {
          sessionStorage.setItem('signupStep', '2');
          sessionStorage.setItem('signupUser', JSON.stringify(user));
          navigate('/terms-of-use');
        }}
      >
        {t('termsOfUse:title')}{' '}
      </Box>
      <Box sx={styles.checkBoxText}>{t('create_account_terms_end')}</Box>
    </Typography>
  );

  const inputFields = (
    <Stack spacing={2}>
      {controlledTextView(
        <TextField
          hasAdornmentEnd
          value={user.password}
          title={t('create_account_password')}
          placeholder={t('create_account_password')}
          disabled={loading}
        />,
        'password'
      )}
      {controlledTextView(
        <TextField
          hasAdornmentEnd
          value={confirmPassword}
          title={t('create_account_confirm_password')}
          placeholder={t('create_account_confirm_password')}
          disabled={loading}
        />,
        'confirmPassword',
        setConfirmPassword
      )}
    </Stack>
  );

  const itemInStack = (
    item: string,
    length: boolean,
    number: boolean,
    letter: boolean
  ) => (
    <Box sx={styles.stackLineItems}>
      {(length && hasMinLength(password)) ||
      (number && hasNumber(password)) ||
      (letter && hasLetter(password)) ? (
        <Check color="success" />
      ) : (
        <Close color="error" />
      )}
      <Typography sx={styles.requiredItems}>{item}</Typography>
    </Box>
  );

  const requiredInPassword = (
    <Box>
      <Typography sx={styles.requiredTitle}>
        {t('create_account_required_title')}
      </Typography>
      <Stack sx={styles.stackItems}>
        {itemInStack(t('create_account_length'), true, false, false)}
        {itemInStack(t('create_account_one_number'), false, true, false)}
        {itemInStack(t('create_account_one_letter'), false, false, true)}
      </Stack>
    </Box>
  );

  const checkBox = (
    <Box sx={styles.checkBoxWithText}>
      <Checkbox
        checked={allowTermsConditions}
        disabled={loading}
        onChange={(e) => {
          setAllowTermsCondition(e.target.checked);
        }}
        sx={styles.checkBox}
      />
      {customPermissions}
    </Box>
  );

  return (
    <Stack
      spacing={4}
      sx={styles.container}
      component="form"
      onSubmit={handleSubmit(onSubmit)}
    >
      {inputFields}
      {requiredInPassword}
      {checkBox}
      <WarningField
        description={warningMessage}
        isHidden={!showWarning}
        setShowWarning={setShowWarning}
        status="warning"
        rightButtonTitle={t('close')}
        rightButtonAction={() => setShowWarning(false)}
      />
      <Button
        title={t('common:next')}
        onClick={handleSubmit(onSubmit)}
        disable={!isValid || !allowTermsConditions || loading}
        loading={loading}
      />
    </Stack>
  );
};

export default SignUpPassword;
